/* eslint-disable max-lines */
import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input
} from '@angular/core';
import { Observable, of, zip } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import groupBy from 'lodash-es/groupBy';

import { TravelBookingType } from '@pax/entities/travel-booking-types';
import {
  AutoBookingPayload, SubmitBookingArrangementProps, Travelers
} from '@booking/entities/booking-arrangement';
import { VehicleDriver } from '@autos/entities/vehicle-booking.payload';
import { nightsLabel, totalNights } from '@ux/mappers/guest.mapper';
import { PaxType } from '@booking/entities/pax-type';
import { AirValidationBySectorResponse, AirValidationResponse } from '@app/entities/air-validation.response';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { AirValidationVmPipe } from '@booking/pipes/air-validation-vm.pipe';
import { cabinTypeNameMapper } from '@flight-results/mappers/flights.mapper';
import { GUEST_PAYLOAD_KEY } from '@booking/mappers/hotel-booking.payload.mapper';
import { PlaceNamePipe } from '@app/pipes/place-name.pipe';
import { PlaceSelectors } from '@app/entities/place';
import { CapitalizeAllPipe } from '@app/pipes/capitalize-all.pipe';
import {
  ActivitiesBookPayloadVM, ActivitiesValidateResponse, ActivityFareBookParamsVM
} from '@activities/entities/activities-booking';
import { getActivitiesBookingFareParam } from '@activities/activities-results/mappers/activities-routing.mapper';
import { NavigationService } from '@app/services/navigation.service';
import { getGroupAttributes } from '@app/modules/activities/mappers/fare-attributes.mapper';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { isNullOrUndefined } from '@app/util/util';

@Component({
  selector: 'app-waiting-product-info',
  templateUrl: './waiting-product-info.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class WaitingProductInfoComponent {

  @Input() props: SubmitBookingArrangementProps;
  @Input() type: TravelBookingType;
  @Input() isMobile: boolean;
  @Input() airValidation: AirValidationResponse;
  @Input() airValidationBySector: AirValidationBySectorResponse;
  @Input() hotelValidation: HotelValidationResponse;
  @Input() autoValidation: VehicleValidateResponse;
  @Input() activitiesValidation: ActivitiesValidateResponse;
  @Input() isMultipleCarriers: boolean;
  @Input() capacity: number;

  public readonly icon = Icon;
  public readonly iconColor = IconFillColor;
  public readonly iconSize = 20;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  constructor(
    private readonly airValidationVmPipe: AirValidationVmPipe,
    private readonly translateService: TranslocoService,
    private readonly placeNamePipe: PlaceNamePipe,
    private readonly capitalizeAllPipe: CapitalizeAllPipe,
    private readonly navigationService: NavigationService, // Temporal logic until Netactica solution
  ) { }

  get smallDetail$(): Observable<string[]> {
    switch (this.type) {
      case TravelBookingType.AUTOS:
        return of([this.autoValidation?.VehicleOption?.Vehicle?.VehicleCategory ?? '']);
      case TravelBookingType.FLIGHTS:
        const cabinKey = this.airValidation?.TripOption?.Sectors?.[0]?.Flights?.[0]?.Cabin ?
          cabinTypeNameMapper(this.airValidation?.TripOption?.Sectors?.[0]?.Flights?.[0]?.Cabin) : '';
        return this.translateService.selectTranslate(`FLIGHT_RESULTS_MODULE.FLIGHT_CLASSES.${cabinKey}`)
          .pipe(map(classByFlight => [this.capitalizeAllPipe.transform(classByFlight)]));
      case TravelBookingType.FLIGHTS_BY_SECTOR:
        const cabinBsKey = this.airValidationBySector?.AirValidateResponse?.[0]?.TripOption?.Sectors?.[0]?.Flights?.[0]?.Cabin ?
          cabinTypeNameMapper(
            this.airValidationBySector?.AirValidateResponse?.[0]?.TripOption?.Sectors?.[0]?.Flights?.[0]?.Cabin
          ) : '';
        return this.translateService.selectTranslate(`FLIGHT_RESULTS_MODULE.FLIGHT_CLASSES.${cabinBsKey}`)
          .pipe(map(classBySector => [this.capitalizeAllPipe.transform(classBySector)]));
      case TravelBookingType.BUSES:
        const code = this.airValidation?.TripOption?.Sectors?.[0]?.Flights?.[0]?.DepartureAirport;
        return zip(
          this.placeNamePipe.transform(code, PlaceSelectors.City, true),
          this.placeNamePipe.transform(code, PlaceSelectors.Country, true),
          this.placeNamePipe.transform(code, PlaceSelectors.AlternativeName, true),
        ).pipe(
          map(([city, country, name]) => {
            const cityLabel = `, ${city}`;
            const countryLabel = `, ${country}`;
            return [`${name}${cityLabel}${countryLabel}`];
          })
        );
      case TravelBookingType.PACKAGE_AIR:
      case TravelBookingType.HOTELS:
        return of(this.getRoomLabelList(this.hotelValidation));
      case TravelBookingType.ACTIVITIES:
        return of(!isNullOrUndefined(this.activityTime) ? [this.activityTime] : []);
      default:
        return of(['']);
    }
  }

  get startDate(): string {
    switch (this.type) {
      case TravelBookingType.AUTOS:
        return this.autoValidation?.VehicleOption?.PickUp?.Time ?? '';
      case TravelBookingType.BUSES:
      case TravelBookingType.FLIGHTS:
      case TravelBookingType.PACKAGE_AIR:
        return this.getAirDateByIndex(this.airValidation, 0) ?? '';
      case TravelBookingType.FLIGHTS_BY_SECTOR:
        return this.getAirDateByIndex(this.airValidationBySector, 0) ?? '';
      case TravelBookingType.HOTELS:
        return this.hotelValidation?.HotelReservation?.Checkin ?? '';
      case TravelBookingType.ACTIVITIES:
        return this.startDateActivities;
      default:
        return ''
    }
  }

  get endDate(): string {
    switch (this.type) {
      case TravelBookingType.AUTOS:
        return this.autoValidation?.VehicleOption?.DropOff?.Time ?? '';
      case TravelBookingType.BUSES:
      case TravelBookingType.FLIGHTS:
      case TravelBookingType.PACKAGE_AIR:
        return this.airValidation?.TripOption?.Sectors?.length > 1 ? this.getAirDateByIndex(this.airValidation, 1) ?? '' : '';
      case TravelBookingType.FLIGHTS_BY_SECTOR:
        if (
          this.isMultipleCarriers || (
            this.airValidationBySector?.AirValidateResponse?.length === 1 &&
            this.airValidationBySector?.AirValidateResponse?.[0]?.TripOption?.Sectors?.length > 1
          )
        ) {
          return this.getAirDateByIndex(this.airValidationBySector, 1) ?? '';
        } else {
          return '';
        }
      case TravelBookingType.HOTELS:
        return this.hotelValidation?.HotelReservation?.Checkout ?? '';
      default:
        return '';
    }
  }

  get activityTime(): string {
    const group = getGroupAttributes(this.activitiesValidation?.Products?.ProductsDetails?.[0]?.Fares)
    return group?.Times?.[0]?.Value;
  }

  get totalNightsNumber(): number {
    return this.showLengthOfStay ? totalNights(this.endDate, this.startDate) : 0;
  }

  get nights(): string {
    return this.showLengthOfStay ? nightsLabel(this.totalNightsNumber) : '';
  }

  get smallDetailIcon(): Icon {
    switch (this.type) {
      case TravelBookingType.FLIGHTS:
      case TravelBookingType.FLIGHTS_BY_SECTOR:
        return Icon.Flight1;
      case TravelBookingType.HOTELS:
      case TravelBookingType.PACKAGE_AIR:
        return Icon.Bed;
      case TravelBookingType.BUSES:
        return Icon.Buses;
      case TravelBookingType.AUTOS:
        return Icon.Autos;
      case TravelBookingType.ACTIVITIES:
        return Icon.Clock;
      default:
        return null;
    }
  }

  get showTravelerQuantityDetail(): boolean {
    return this.type === TravelBookingType.HOTELS;
  }

  get showLengthOfStay(): boolean {
    return this.type === TravelBookingType.HOTELS || this.type === TravelBookingType.PACKAGE_AIR;
  }

  get travelers(): Travelers[] | VehicleDriver[] {
    switch (this.type) {
      case TravelBookingType.AUTOS:
        return (this.props as AutoBookingPayload)?.arrangement?.Driver ?
          [(this.props as AutoBookingPayload).arrangement.Driver] : [];
      case TravelBookingType.ACTIVITIES:
        return (this.props as ActivitiesBookPayloadVM)?.arrangement?.Passengers ?? [];
      default:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (this.props as any)?.arrangement?.Travelers ?? [];
    }
  }

  get adults(): number {
    return this.getPassengersByType(PaxType.ADULTS);
  }

  get getAdultsTranslate(): string {
    return this.adults > 1 ? 'MANY_ADULTS' : 'ADULT';
  }

  get getGuestsTranslate(): string {
    return this.capacity > 1 ? 'MANY_GUESTS' : 'GUEST';
  }

  get childrens(): number {
    return this.getPassengersByType(PaxType.CHILDREN);
  }

  get getChildrenTranslate(): string {
    return this.childrens > 1 ? 'MANY_CHILDRENS' : 'CHILD';
  }

  get infants(): number {
    return this.getPassengersByType(PaxType.INFANTS);
  }

  get getInfantTranslate(): string {
    return this.infants > 1 ? 'MANY_INFANTS' : 'INFANT';
  }

  get isByhours(): boolean {
    return this.hotelValidation?.HotelReservation?.MicroStay;
  }

  get packHours(): number {
    return this.hotelValidation?.HotelReservation?.Rooms?.[0]?.PackHour;
  }
  
  // Temporal logic until Netactica solution
  get startDateActivities(): string {
    return this.faresVm?.[0]?.SelectedDate;
  }

  // Temporal logic until Netactica solution
  get faresVm(): ActivityFareBookParamsVM[] {
    return getActivitiesBookingFareParam(this.navigationService.params?.rateId);
  }


  public hiddenTraveler(traveler: Travelers): boolean {
    const regExp = new RegExp(`^${GUEST_PAYLOAD_KEY}`, 'i');
    return regExp.test(traveler.DocumentNumber) && regExp.test(traveler.FirstName) && regExp.test(traveler.LastName);
  }

  private getAirDateByIndex(response: AirValidationResponse | AirValidationBySectorResponse, index: number): string {
    switch (this.type) {
      case TravelBookingType.BUSES:
      case TravelBookingType.FLIGHTS:
      case TravelBookingType.PACKAGE_AIR:
        return (response as AirValidationResponse)?.TripOption ? this.airValidationVmPipe.transform(
          response as AirValidationResponse
        )?.TripOption?.Sectors?.[index]?.Flights?.[0]?.DepartureDate : '';
      case TravelBookingType.FLIGHTS_BY_SECTOR:
        if (this.isMultipleCarriers) {
          return (response as AirValidationBySectorResponse)?.AirValidateResponse?.[index]?.TripOption ?
            this.airValidationVmPipe.transform(
              (response as AirValidationBySectorResponse)?.AirValidateResponse?.[index]
            )?.TripOption?.Sectors?.[0]?.Flights?.[0]?.DepartureDate : '';
        } else {
          return (response as AirValidationBySectorResponse)?.AirValidateResponse?.[0]?.TripOption ?
            this.airValidationVmPipe.transform(
              (response as AirValidationBySectorResponse)?.AirValidateResponse?.[0]
            )?.TripOption?.Sectors?.[index]?.Flights?.[0]?.DepartureDate : '';
        }
      default:
        return '';
    }
  }

  private getRoomLabelList(hotelValidation: HotelValidationResponse): string[] {
    const rooms = hotelValidation?.HotelReservation?.Rooms ?? [];
    const roomsGroupBy = groupBy(rooms, 'Description');
    const fullDescription = [];
    for (const roomDescription in roomsGroupBy) {
      const quantity = roomsGroupBy?.[roomDescription]?.length ?? 0;
      if (quantity > 1) {
        fullDescription.push(`${roomDescription} <span class="room-type-quantity">x${quantity}</span>`);
      } else {
        fullDescription.push(roomDescription);
      }
    }
    return fullDescription;
  }

  private getPassengersByType(type: string): number {
    return [...this.travelers].filter(passenger => passenger['PaxType'] ? passenger['PaxType'] === type : true).length;
  }

}
